import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Coins, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Gift drawer — slides up from the bottom on a TikTok-style feed.
 * Shows the static catalog grouped by tier, the viewer's current coin
 * balance, and an inline "out of coins" CTA when the balance falls short.
 *
 * Props
 *   open        — bool, controls visibility
 *   onClose     — () => void
 *   videoId     — string, target plyx_video_id
 *   onGiftSent  — ({emoji, coins, new_balance}) => void
 *                  parent uses this to spawn a floating-emoji animation
 */
const TIER_LABELS = {
  starter:   { label: "Starter",      accent: "#FFB42D" },
  warm:      { label: "Warm",         accent: "#FF2D7A" },
  premium:   { label: "Premium",      accent: "#A45DFF" },
  legendary: { label: "Legendary",    accent: "#00E0C7" },
  royal:     { label: "Royal Africa", accent: "#FFD700" },
  icon:      { label: "Icon",         accent: "#FF6B00" },
};

const GiftDrawer = ({ open, onClose, videoId, liveRoomName, onGiftSent, targetUserId, targetLabel }) => {
  const { plyxUser, refresh } = usePlyxAuth();
  const [catalog, setCatalog] = useState([]);
  const [balance, setBalance] = useState(plyxUser?.coin_balance ?? 0);
  const [sending, setSending] = useState(null);     // gift code currently sending
  const [loading, setLoading] = useState(true);

  // Where do we send gifts/tips? Live streams use /plyx/live/{room}/{gift,tip};
  // regular videos use /plyx/videos/{id}/{gift,tip}. Backward-compatible: if
  // liveRoomName is omitted, behaviour is identical to before.
  const giftUrl = liveRoomName
    ? `/plyx/live/${liveRoomName}/gift`
    : `/plyx/videos/${videoId}/gift`;
  const tipUrl = liveRoomName
    ? `/plyx/live/${liveRoomName}/tip`
    : `/plyx/videos/${videoId}/tip`;

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [cat, me] = await Promise.all([
          plyxApi.get("/plyx/gifts/catalog"),
          plyxApi.get("/plyx/coins/me"),
        ]);
        if (!alive) return;
        setCatalog(cat.data.gifts || []);
        setBalance(me.data.balance);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't load gifts");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const sendGift = async (g, multiplier = 1) => {
    if (sending) return;
    const totalCost = g.coins * multiplier;
    if (balance < totalCost) {
      toast.error(`Need ${totalCost} coins — you have ${balance}. Earn more by watching!`);
      return;
    }
    setSending(g.code);
    try {
      let newBal = balance;
      let actualSent = 0;
      // Fire-and-await sequentially so we never overspend; abort the
      // moment the server says we ran out.
      for (let i = 0; i < multiplier; i++) {
        try {
          const r = await plyxApi.post(giftUrl, { gift_code: g.code, target_user_id: targetUserId || undefined });
          newBal = r.data.new_balance;
          actualSent += 1;
          onGiftSent?.({ emoji: g.emoji, coins: g.coins, new_balance: newBal });
          if (multiplier > 1) {
            // Tiny stagger between shower emojis.
            await new Promise((res) => setTimeout(res, 80));
          }
        } catch (innerErr) {
          // Out of coins mid-shower — stop here.
          break;
        }
      }
      setBalance(newBal);
      if (actualSent > 0) {
        toast.success(
          multiplier > 1
            ? `${g.emoji}×${actualSent} sent! · ${g.coins * actualSent} coins`
            : `${g.emoji} sent! · ${g.coins} coins`,
        );
      } else {
        toast.error("Couldn't send any gifts — balance changed.");
      }
      refresh?.();
    } finally {
      setSending(null);
    }
  };

  // Long-press handler — triggers a x10 combo. Mouse + touch.
  const longPressRef = React.useRef({ timer: null, fired: false, gift: null });
  const onPressStart = (g) => {
    if (sending) return;
    longPressRef.current.fired = false;
    longPressRef.current.gift = g;
    longPressRef.current.timer = setTimeout(() => {
      longPressRef.current.fired = true;
      sendGift(g, 10);
    }, 550);
  };
  const onPressEnd = (g) => {
    if (longPressRef.current.timer) {
      clearTimeout(longPressRef.current.timer);
      longPressRef.current.timer = null;
    }
    // Single tap → send 1x. The button's onClick already does this, so this
    // branch is only relevant when the user released BEFORE the timer fired.
    if (!longPressRef.current.fired && longPressRef.current.gift && longPressRef.current.gift.code === g.code) {
      // intentionally no-op; onClick handles single-tap send.
    }
    longPressRef.current.fired = false;
    longPressRef.current.gift = null;
  };

  if (!open) return null;

  const grouped = catalog.reduce((acc, g) => {
    (acc[g.tier] = acc[g.tier] || []).push(g);
    return acc;
  }, {});

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="plyx-gift-drawer-backdrop"
      />
      <div
        className="fixed inset-x-0 bottom-0 z-[61] rounded-t-3xl bg-[#0A0A0A] text-white border-t border-white/10 max-h-[78vh] overflow-hidden flex flex-col"
        style={{ animation: "plyxSlideUp 320ms cubic-bezier(0.22,1,0.36,1)" }}
        data-testid="plyx-gift-drawer"
      >
        {/* Handle + header */}
        <div className="flex items-center justify-between px-5 pt-3 pb-2">
          <div className="w-10 h-1.5 rounded-full bg-white/15 mx-auto" />
        </div>
        <div className="flex items-center justify-between px-5 pb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/60">Send a gift</div>
            <div className="text-sm text-white/80 mt-0.5">Creators get 70% of the value to their Plyx wallet.</div>
            <div className="text-[10px] text-white/40 mt-1">Tip: long-press any gift to send ×10 in a row.</div>
          </div>
          <button onClick={onClose} aria-label="Close gift drawer"
            className="w-9 h-9 rounded-full bg-white/8 flex items-center justify-center hover:bg-white/15"
            data-testid="plyx-gift-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Balance pill */}
        <div className="px-5 pb-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white text-sm font-semibold"
               data-testid="plyx-gift-balance">
            <Coins className="w-3.5 h-3.5" />
            {balance.toLocaleString()} coins
          </div>
        </div>

        {targetLabel && (
          <div className="px-5 -mt-2 mb-3" data-testid="plyx-gift-target-banner">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white text-[12px] font-semibold border border-white/15">
              <span className="opacity-70">Gifting</span>
              <span>{targetLabel}</span>
            </div>
          </div>
        )}

        {/* Custom-amount tip */}
        <CustomTipRow
          tipUrl={tipUrl}
          balance={balance}
          targetUserId={targetUserId}
          onSent={(payload) => {
            setBalance(payload.new_balance);
            onGiftSent?.({
              emoji: "💸",
              coins: payload.coins_spent,
              new_balance: payload.new_balance,
              tip_train: payload.tip_train,  // forward tip-train signal
            });
            refresh?.();
          }}
        />

        {/* Catalog */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin opacity-60" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 pb-8">
            {Object.entries(grouped).map(([tier, items]) => (
              <div key={tier} className="mt-4">
                <div className="text-[11px] uppercase tracking-widest mb-2"
                     style={{ color: TIER_LABELS[tier]?.accent || "#fff" }}>
                  {TIER_LABELS[tier]?.label || tier}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {items.map((g) => {
                    const affordable = balance >= g.coins;
                    const isSending = sending === g.code;
                    const isPremiumTier = g.tier === "royal" || g.tier === "icon";
                    const tierAccent = TIER_LABELS[g.tier]?.accent || "#FFB42D";
                    return (
                      <button
                        key={g.code}
                        onClick={() => {
                          // Only fire the 1x send if a long-press didn't take over.
                          if (longPressRef.current.fired) return;
                          sendGift(g, 1);
                        }}
                        onMouseDown={() => onPressStart(g)}
                        onMouseUp={() => onPressEnd(g)}
                        onMouseLeave={() => onPressEnd(g)}
                        onTouchStart={() => onPressStart(g)}
                        onTouchEnd={() => onPressEnd(g)}
                        onContextMenu={(e) => e.preventDefault()}
                        disabled={isSending || !affordable}
                        data-testid={`plyx-gift-${g.code}`}
                        title={`${g.label}${g.gbp_label ? ` (${g.gbp_label})` : ""} · ${g.coins.toLocaleString()} coins · long-press for ×10`}
                        className={`group relative rounded-2xl p-3 border transition-all flex flex-col items-center
                                    ${affordable ? "hover:scale-105" : "opacity-40"}
                                    ${isPremiumTier && affordable ? "border-2 shadow-lg" : "border border-white/10"}`}
                        style={{
                          background: isPremiumTier && affordable
                            ? `linear-gradient(135deg, ${tierAccent}22, #0A0A0A 60%, ${tierAccent}11)`
                            : (affordable
                                ? "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))"
                                : "rgba(255,255,255,0.02)"),
                          borderColor: isPremiumTier && affordable ? tierAccent : undefined,
                          boxShadow: isPremiumTier && affordable ? `0 0 16px ${tierAccent}33` : undefined,
                        }}
                      >
                        <span className="text-3xl select-none pointer-events-none" style={{
                          filter: affordable ? `drop-shadow(0 0 ${isPremiumTier ? "12px" : "8px"} ${tierAccent}88)` : "grayscale(40%)",
                          transition: "transform 200ms cubic-bezier(0.22,1,0.36,1)",
                        }}>{g.emoji}</span>
                        <span className="text-[10px] mt-1 text-white/70 text-center leading-tight">{g.label}</span>
                        {g.gbp_label ? (
                          <span className="text-[10px] mt-0.5 font-bold" style={{ color: tierAccent }}>
                            {g.gbp_label}
                          </span>
                        ) : (
                          <span className="text-[11px] mt-1 inline-flex items-center gap-1 font-semibold text-[#FFB42D]">
                            <Coins className="w-2.5 h-2.5" />{g.coins.toLocaleString()}
                          </span>
                        )}
                        {isSending && (
                          <span className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Out-of-coins CTA at the bottom */}
            <div className="mt-6 rounded-xl p-4 border border-white/10 bg-white/5 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold">Need more coins?</div>
                <div className="text-[11px] text-white/60 mt-0.5">Top up in seconds or keep watching to earn.</div>
              </div>
              <Link
                to="/plyx/wallet"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-white text-xs font-semibold hover:opacity-90"
                onClick={onClose}
                data-testid="plyx-gift-buy-coins"
              >
                Buy coins <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes plyxSlideUp {
          0% { transform: translateY(100%); }
          100% { transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default GiftDrawer;

// ── Custom-amount tip row ────────────────────────────────────────────────
// Inline mini-form for sending any coin amount between 5–50,000 to the
// creator. Hits POST /api/plyx/videos/:id/tip and bubbles the new balance
// to the parent drawer so the catalog buttons stay enabled/disabled
// correctly.
const CustomTipRow = ({ tipUrl, balance, onSent, targetUserId }) => {
  const [coins, setCoins] = useState(50);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const PRESETS = [25, 100, 500, 2000];
  const cost = Math.max(5, Math.min(50_000, Number(coins) || 0));
  const affordable = balance >= cost;

  const send = async () => {
    if (sending) return;
    if (!affordable) { toast.error("Not enough coins."); return; }
    setSending(true);
    try {
      const { data } = await plyxApi.post(tipUrl, {
        coins: cost,
        note: note.trim() || null,
        target_user_id: targetUserId || undefined,
      });
      toast.success(`Tipped ${data.coins_spent} coins ✨`);
      setNote("");
      onSent?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tip failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-5 pb-2" data-testid="plyx-custom-tip-row">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFB42D]">Custom tip</div>
          <div className="text-[10px] text-white/40">5 – 50,000 coins</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="5"
            max="50000"
            step="1"
            value={coins}
            onChange={(e) => setCoins(e.target.value)}
            data-testid="plyx-custom-tip-input"
            className="flex-1 min-w-0 rounded-xl bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FFB42D]"
            aria-label="Tip amount in coins"
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !affordable || cost < 5}
            data-testid="plyx-custom-tip-send"
            className="px-4 py-2 rounded-xl font-bold text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
          >
            {sending ? "…" : `Send ${cost}`}
          </button>
        </div>
        <div className="mt-2 flex gap-1.5 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setCoins(p)}
              data-testid={`plyx-custom-tip-preset-${p}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                cost === p ? "bg-white text-white border-white" : "border-white/15 text-white/65 hover:text-white hover:border-white/30"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
        <input
          type="text"
          maxLength={140}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="plyx-custom-tip-note"
          placeholder="Add a note (optional)"
          className="mt-2 w-full rounded-xl bg-black/40 border border-white/15 px-3 py-1.5 text-xs outline-none focus:border-[#FFB42D] placeholder:text-white/30"
        />
        {!affordable && (
          <div className="mt-2 text-[11px] text-white/50">Not enough coins — top up below.</div>
        )}
      </div>
    </div>
  );
};
